import type {
  Login,
  SessionState,
  SetupComplete,
  SetupStatus,
} from "@vekha/contracts";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import {
  ApiRequestError,
  completeSetup,
  describeApiError,
  fetchAuthenticationState,
  login,
  logout,
} from "./api";
import { setupTokenIsExpired } from "./setup";
import { forgetDialogsContext } from "./dialogs-list-reads";
import { takeRead } from "./startup-reads";

type AuthMode =
  | "checking"
  | "unavailable"
  | "setup"
  | "setup-expired"
  | "login"
  | "authenticated";

type AuthResolution = {
  mode: Exclude<AuthMode, "checking">;
  session: SessionState | null;
  setupExpiresAt: string | null;
  error: string | null;
  failure: AuthFailure | null;
};

type AuthFailure = Readonly<{
  kind: "network" | "invalid_response" | "unauthorized" | "request";
  code: string;
  requestId: string | null;
  attemptedAt: string;
  retryable: boolean;
}>;

type AuthResolutionDependencies = {
  readAuthenticationState: () => Promise<{
    setup: SetupStatus;
    session: SessionState | null;
  }>;
};

type AuthenticationRecoveryInput = AuthResolutionDependencies & Readonly<{
  onResolution: (resolution: AuthResolution) => void;
  now?: () => Date;
}>;

const AUTH_RECOVERY_DELAYS_MS = [1_500, 3_000, 7_000, 15_000, 30_000] as const;

type AuthContextValue = {
  mode: AuthMode;
  session: SessionState | null;
  setupExpiresAt: string | null;
  error: string | null;
  pending: boolean;
  signIn: (input: Login) => Promise<boolean>;
  finishSetup: (input: SetupComplete) => Promise<boolean>;
  signOut: () => Promise<boolean>;
  markMfaEnrolled: () => void;
  retryAuthentication: () => void;
};

const AuthContext = createContext<AuthContextValue | null>(null);

export async function resolveInitialAuthState({
  readAuthenticationState = fetchAuthenticationState,
}: Partial<AuthResolutionDependencies> = {}, now: () => Date = () => new Date()): Promise<AuthResolution> {
  try {
    const state = await readAuthenticationState();
    const status = state.setup;
    if (status.required) {
      return {
        mode: setupTokenIsExpired(status.expiresAt) ? "setup-expired" : "setup",
        session: null,
        setupExpiresAt: status.expiresAt,
        error: null,
        failure: null,
      };
    }

    const current = state.session;
    return {
      mode: current ? "authenticated" : "login",
      session: current,
      setupExpiresAt: null,
      error: null,
      failure: null,
    };
  } catch (caught) {
    const failure = authenticationFailure(caught, now());
    return {
      mode: failure.kind === "unauthorized" ? "login" : "unavailable",
      session: null,
      setupExpiresAt: null,
      error: describeAuthenticationFailure(caught, failure),
      failure,
    };
  }
}

function authenticationFailure(caught: unknown, attemptedAt: Date): AuthFailure {
  const error = caught instanceof ApiRequestError ? caught : null;
  const code = error?.code ?? "AUTH_STATE_REQUEST_FAILED";
  const kind = error?.status === 401
    ? "unauthorized"
    : code === "AUTH_STATE_INVALID_RESPONSE"
      ? "invalid_response"
      : error?.status === null || error === null
        ? "network"
        : "request";
  return {
    kind,
    code,
    requestId: error?.requestId ?? null,
    attemptedAt: attemptedAt.toISOString(),
    retryable: kind === "network"
      || kind === "invalid_response"
      || (kind === "request" && error !== null && (
        error.status === 408
        || error.status === 429
        || (error.status !== null && error.status >= 500)
      )),
  };
}

function describeAuthenticationFailure(caught: unknown, failure: AuthFailure): string {
  const kindLabel = failure.kind === "network"
    ? "Сбой сети"
    : failure.kind === "invalid_response"
      ? "Некорректный ответ сервера"
      : failure.kind === "unauthorized"
        ? "Сессия истекла или недействительна"
        : "Запрос отклонён сервером";
  const identity = failure.requestId
    ? `request ID ${failure.requestId}`
    : `код ${failure.code}`;
  const attemptedAt = new Intl.DateTimeFormat("ru-RU", {
    dateStyle: "short",
    timeStyle: "medium",
  }).format(new Date(failure.attemptedAt));
  return `${kindLabel}. ${describeApiError(caught)} Техническая причина: ${identity}. Последняя попытка: ${attemptedAt}.`;
}

function authenticationFailureIsRetryable(failure: AuthFailure): boolean {
  return failure.retryable;
}

export function startAuthenticationRecovery({
  readAuthenticationState,
  onResolution,
  now = () => new Date(),
}: AuthenticationRecoveryInput): () => void {
  let cancelled = false;
  let timer: ReturnType<typeof setTimeout> | null = null;
  let retryIndex = 0;

  const attempt = async () => {
    const resolution = await resolveInitialAuthState({ readAuthenticationState }, now);
    if (cancelled) return;
    onResolution(resolution);
    if (!resolution.failure || !authenticationFailureIsRetryable(resolution.failure)) return;
    if (retryIndex >= AUTH_RECOVERY_DELAYS_MS.length) return;
    timer = globalThis.setTimeout(() => {
      void attempt();
    }, AUTH_RECOVERY_DELAYS_MS[retryIndex++]!);
  };

  void attempt();
  return () => {
    cancelled = true;
    if (timer !== null) globalThis.clearTimeout(timer);
  };
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [mode, setMode] = useState<AuthMode>("checking");
  const [session, setSession] = useState<SessionState | null>(null);
  const [setupExpiresAt, setSetupExpiresAt] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState(false);
  const [checkRevision, setCheckRevision] = useState(0);

  useEffect(() => {
    return startAuthenticationRecovery({
      // The first check is the one main.tsx started with the page (startup-reads); retries read anew.
      readAuthenticationState: () => takeRead("auth", fetchAuthenticationState),
      onResolution: (resolution) => {
        setMode(resolution.mode);
        setSession(resolution.session);
        setSetupExpiresAt(resolution.setupExpiresAt);
        setError(resolution.error);
      },
    });
  }, [checkRevision]);

  const retryAuthentication = useCallback(() => {
    setMode("checking");
    setSession(null);
    setSetupExpiresAt(null);
    setError(null);
    setCheckRevision((current) => current + 1);
  }, []);

  const signIn = useCallback(async (input: Login) => {
    setPending(true);
    setError(null);
    try {
      const state = await login(input);
      setSession(state);
      setSetupExpiresAt(null);
      setMode("authenticated");
      return true;
    } catch (caught) {
      setError(describeApiError(caught));
      return false;
    } finally {
      setPending(false);
    }
  }, []);

  const finishSetup = useCallback(async (input: SetupComplete) => {
    setPending(true);
    setError(null);
    try {
      const state = await completeSetup(input);
      setSession(state);
      setSetupExpiresAt(null);
      setMode("authenticated");
      return true;
    } catch (caught) {
      setError(describeApiError(caught));
      return false;
    } finally {
      setPending(false);
    }
  }, []);

  const signOut = useCallback(async () => {
    if (!session) return false;
    setPending(true);
    setError(null);
    try {
      await logout(session.csrfToken);
      forgetDialogsContext();
      setSession(null);
      setMode("login");
      return true;
    } catch (caught) {
      setError(describeApiError(caught));
      return false;
    } finally {
      setPending(false);
    }
  }, [session]);

  const markMfaEnrolled = useCallback(() => {
    setSession((current) => (current ? { ...current, mfaEnrolled: true } : current));
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({
      mode,
      session,
      setupExpiresAt,
      error,
      pending,
      signIn,
      finishSetup,
      signOut,
      markMfaEnrolled,
      retryAuthentication,
    }),
    [
      error,
      finishSetup,
      markMfaEnrolled,
      mode,
      pending,
      retryAuthentication,
      session,
      setupExpiresAt,
      signIn,
      signOut,
    ],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext);
  if (!context) throw new Error("useAuth must be used inside AuthProvider");
  return context;
}
